"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { Modal } from "./Modal";
import { Button } from "./Button";
import { useInjectStyles } from "../utils/useInjectStyles";

/**
 * 危險程度。刻意分三級——每一級都用最重的樣式等於沒有分級，使用者會對警示
 * 麻痺（與 danger 按鈕的「危險疲勞」是同一個道理）。
 *
 * 判斷準則是兩軸交叉，不是憑感覺：
 *   是否可復原  → 決定要不要圖示、要不要把預設焦點移開確認鈕
 *   影響筆數/範圍 → 決定要不要加輸入門檻
 *
 *   low    可逆、單筆（軟刪除）      無圖示，預設焦點在確認鈕
 *   medium 不可逆、單筆（硬刪除）    琥珀警示圖示，預設焦點在**取消**鈕
 *   high   批次／永久清除            紅色危險圖示 + 需輸入筆數才解鎖確認鈕
 */
export type ConfirmLevel = "low" | "medium" | "high";

export interface ConfirmDialogProps {
  open: boolean;
  /** 標題。慣例用具名疑問句「確定要刪除「{名稱}」嗎？」，讓使用者不必讀內文就能核對對象。 */
  title: ReactNode;
  /** 後果 / 可逆性 / 影響範圍。軟刪與硬刪的說法必須不同，不可一律寫「無法復原」。 */
  children: ReactNode;
  level?: ConfirmLevel;
  /**
   * 確認鈕文字。預設「刪除」——用語對照表 #153/#392 的官方譯文。
   * ⚠️ 不要傳「確定」：對照表查無此詞條（只有「確認」#575）。
   * 按鈕文字應命名該按鈕的實際動作，非刪除類請傳該動詞本身。
   */
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  /** 非同步執行中：整個對話框鎖住，唯一離開路徑是等結果回來。 */
  busy?: boolean;
  /** busy 時確認鈕的文字，如「刪除中…」。 */
  busyLabel?: string;
  /** level="high" 時要求使用者輸入的筆數，輸入相符才解鎖確認鈕。 */
  requireCount?: number;
}

const LEVEL_ICON: Record<ConfirmLevel, string | null> = {
  low: null,
  medium: "!",
  high: "⚠",
};

export function ConfirmDialog({
  open,
  title,
  children,
  level = "low",
  confirmLabel = "刪除",
  onConfirm,
  onCancel,
  busy = false,
  busyLabel,
  requireCount,
}: ConfirmDialogProps) {
  useInjectStyles();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [typed, setTyped] = useState("");

  // 每次開啟都清空輸入，否則上一次打的數字會留著、等於門檻失效。
  useEffect(() => {
    if (open) setTyped("");
  }, [open]);

  const needsTyping = level === "high" && typeof requireCount === "number";
  const unlocked = !needsTyping || typed.trim() === String(requireCount);

  /*
   * 預設焦點：
   *   low    → 確認鈕。可逆且高頻，每次逼使用者多按一次 Tab 是操作疲勞而非安全。
   *   medium → 取消鈕。不可逆，刻意不讓 Enter 連按誤刪。
   *   high   → 輸入框。確認鈕本來就 disabled，沒有誤觸風險。
   */
  const initialFocusRef = level === "high" ? inputRef : level === "medium" ? cancelRef : undefined;

  return (
    <Modal
      open={open}
      // busy 期間鎖住所有離開路徑（Esc、遮罩、✕）：讓使用者在請求飛行中關掉對話框，
      // 會造成「畫面看不到結果、但後端還在處理」的錯覺。
      onClose={busy ? () => {} : onCancel}
      disableOverlayClose={busy}
      title={title}
      initialFocusRef={initialFocusRef}
      footer={
        <>
          {/* 取消在左、危險動作在右：DOM 序＝Tab 序＝視覺序（WCAG 2.4.3），
              且安全的退出路徑在閱讀動線起點，注意力隨動線遞增到最需謹慎的那顆鈕。 */}
          <Button ref={cancelRef} variant="secondary" onClick={onCancel} disabled={busy}>
            取消
          </Button>
          <Button variant="danger-solid" onClick={onConfirm} disabled={busy || !unlocked}>
            {busy ? (busyLabel ?? `${confirmLabel}中…`) : confirmLabel}
          </Button>
        </>
      }
    >
      <div className={`cui-confirm cui-confirm-${level}`}>
        {LEVEL_ICON[level] && (
          // aria-hidden：語意已經在文字裡（「無法復原」），不需要讓螢幕閱讀器
          // 另外唸出一個不明符號。
          <span className="cui-confirm-icon" aria-hidden>
            {LEVEL_ICON[level]}
          </span>
        )}
        <div className="cui-confirm-body">
          {children}
          {needsTyping && (
            <label className="cui-confirm-typing">
              <span>
                請輸入筆數 <b>{requireCount}</b> 以確認：
              </span>
              <input
                ref={inputRef}
                className="cui-input"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                disabled={busy}
                inputMode="numeric"
                autoComplete="off"
                aria-label={`請輸入筆數 ${requireCount} 以確認`}
              />
            </label>
          )}
        </div>
      </div>
    </Modal>
  );
}
